"use client";

import { useState, memo } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import {
  FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE,
  useWaveDecisions,
} from "@/hooks/waves/useWaveDecisions";
import type { ApiWaveDecision } from "@/generated/models/ApiWaveDecision";
import {
  getApprovedWaveDecisionWinners,
  getRenderableWaveDecisionWinners,
} from "@/helpers/waves/wave-decision.helpers";

// Import extracted components
import { WaveWinnerItemSmall } from "./WaveWinnerItemSmall";
import { WaveWinnersSmallLoading } from "./WaveWinnersSmallLoading";
import { WaveWinnersSmallEmpty } from "./WaveWinnersSmallEmpty";
import { WaveWinnersSmallDecisionSelector } from "./WaveWinnersSmallDecisionSelector";
import { useWave } from "@/hooks/useWave";
import { WaveWinnersApprovalError } from "./WaveWinnersApprovalError";

interface WaveWinnersSmallProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}

// Extended decision point type with UI fields
interface EnhancedDecisionPoint extends ApiWaveDecision {
  id: string; // Using decision_time as ID
  date: string; // Formatted date for UI
}

export const WaveWinnersSmall = memo<WaveWinnersSmallProps>(
  ({ wave, onDropClick }) => {
    const {
      decisions: { multiDecision },
      isApproveWave,
      isQuorumWave,
    } = useWave(wave);
    const [selectedDecisionPoint, setSelectedDecisionPoint] = useState<
      string | null
    >(null);

    // Fetch data using decisions endpoint for all waves - same approach as WaveWinners
    const {
      decisionPoints: rawDecisionPoints,
      isFetching,
      isLoadingAllPages,
      isLoadingAllPagesError,
      refetch,
      fetchNextPage,
      hasNextPage,
    } = useWaveDecisions({
      waveId: wave.id,
      enabled: true, // Always enabled now that we use it for both types
      loadAllPages: isApproveWave,
      pageSize: isApproveWave
        ? FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE
        : undefined,
    });
    const isDecisionsLoading = isApproveWave ? isLoadingAllPages : isFetching;
    const contentPresentation = isQuorumWave ? "quorumCompact" : undefined;
    const handleApprovalWinnersRetry = () => {
      if (hasNextPage) {
        void fetchNextPage();
        return;
      }

      void refetch();
    };

    // Process decision points to include UI-friendly fields
    const decisionPoints: EnhancedDecisionPoint[] = rawDecisionPoints.map(
      (point) => {
        const decisionTime = point.decision_time;
        // Create a formatted date string
        const dateObj = new Date(decisionTime);
        return {
          ...point,
          id: decisionTime.toString(), // Use decision_time as ID
          date: dateObj.toISOString(),
        };
      }
    );

    // Derive the effective active decision point during render
    // Falls back to first decision point when selection is null or invalid
    const activeDecisionPoint =
      selectedDecisionPoint &&
      decisionPoints.some((p) => p.id === selectedDecisionPoint)
        ? selectedDecisionPoint
        : (decisionPoints[0]?.id ?? null);

    // Loading state
    if (isDecisionsLoading) {
      return <WaveWinnersSmallLoading />;
    }

    if (isApproveWave && isLoadingAllPagesError) {
      return <WaveWinnersApprovalError onRetry={handleApprovalWinnersRetry} />;
    }

    if (isApproveWave) {
      const approvedWinners = getRenderableWaveDecisionWinners(
        getApprovedWaveDecisionWinners(decisionPoints)
      );

      if (approvedWinners.length === 0) {
        return (
          <WaveWinnersSmallEmpty
            title="No approvals yet"
            message="No drops approved yet"
          />
        );
      }

      return (
        <div className="tw-p-3">
          <div className="tw-flex tw-items-center tw-justify-between tw-px-1">
            <h2 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-50">
              Approved
            </h2>
          </div>

          <div className="tw-mt-3 tw-space-y-3">
            {approvedWinners.map((winner) => (
              <WaveWinnerItemSmall
                key={winner.drop.id}
                drop={convertApiDropToExtendedDrop(winner.drop)}
                wave={wave}
                rank={winner.place}
                onDropClick={() =>
                  onDropClick(convertApiDropToExtendedDrop(winner.drop))
                }
                contentPresentation={contentPresentation}
              />
            ))}
          </div>
        </div>
      );
    }

    // Empty state
    if (decisionPoints.length === 0) {
      return <WaveWinnersSmallEmpty isMultiDecision={multiDecision} />;
    }

    // For single decision waves, just render the first decision point's drops
    if (!multiDecision) {
      const winners = getRenderableWaveDecisionWinners(
        decisionPoints[0]?.winners ?? []
      );

      return (
        <div className="tw-p-3">
          <div className="tw-flex tw-items-center tw-justify-between tw-px-1">
            <h2 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-50">
              Winners
            </h2>
          </div>

          <div className="tw-mt-3 tw-space-y-3">
            {winners.map((winner) => (
              <WaveWinnerItemSmall
                key={winner.drop.id}
                drop={convertApiDropToExtendedDrop(winner.drop)}
                wave={wave}
                rank={winner.place}
                onDropClick={() =>
                  onDropClick(convertApiDropToExtendedDrop(winner.drop))
                }
                contentPresentation={contentPresentation}
              />
            ))}
          </div>
        </div>
      );
    }

    const activeDecisionPointWinners = getRenderableWaveDecisionWinners(
      decisionPoints.find((point) => point.id === activeDecisionPoint)
        ?.winners ?? []
    );

    // For multi-decision waves, add a decision point selector
    return (
      <div className="tw-p-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-1">
          <h2 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-50">
            Winners
          </h2>
        </div>

        {/* Decision point selector */}
        <WaveWinnersSmallDecisionSelector
          decisionPoints={decisionPoints.map((point) => ({
            id: point.id,
            date: point.date,
            winnersCount: point.winners.length,
          }))}
          activeDecisionPoint={activeDecisionPoint}
          onChange={setSelectedDecisionPoint}
        />

        {/* Show winners for selected decision point */}
        <div className="tw-space-y-3">
          {activeDecisionPoint &&
            activeDecisionPointWinners.map((winner) => (
              <WaveWinnerItemSmall
                key={winner.drop.id}
                drop={convertApiDropToExtendedDrop(winner.drop)}
                wave={wave}
                rank={winner.place}
                onDropClick={() =>
                  onDropClick(convertApiDropToExtendedDrop(winner.drop))
                }
                contentPresentation={contentPresentation}
              />
            ))}
        </div>
      </div>
    );
  }
);

WaveWinnersSmall.displayName = "WaveWinnersSmall";
